import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTernaryDarkMode } from "usehooks-ts";

import { DarkModeContext } from "@/hooks/useAppTheme";

export const AppThemeProvider = ({
  children,
  pathname,
}: {
  children: ReactNode;
  pathname: string;
}) => {
  // DARK MODE
  const {
    isDarkMode: isDark,
    toggleTernaryDarkMode,
    setTernaryDarkMode,
    ternaryDarkMode,
  } = useTernaryDarkMode();

  const isDarkMode = isDark;

  // Ensure this useEffect runs only once when the provider mounts
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }
  }, [isDarkMode]);

  // SIDEBAR_EXPANDED
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [isPlanDetailsOpen, setIsPlanDetailsOpen] = useState(false);

  // Memoize functions to prevent recreation
  const expandSidebar = useCallback(() => setIsSidebarExpanded(true), []);
  const collapseSidebar = useCallback(() => setIsSidebarExpanded(false), []);
  const onToggleSidebar = useCallback(
    (value: boolean) => setIsSidebarExpanded(value),
    []
  );
  const onTogglePlanDetails = useCallback(
    () => setIsPlanDetailsOpen((s) => !s),
    []
  );

  // Memoize the context value to prevent unnecessary rerenders
  const contextValue = useMemo(
    () => ({
      isDarkMode,
      toggle: toggleTernaryDarkMode,
      set: setTernaryDarkMode,
      currentMode: ternaryDarkMode,
      isSidebarExpanded,
      expandSidebar,
      collapseSidebar,
      onToggleSidebar,
      isPlanDetailsOpen,
      onTogglePlanDetails,
    }),
    [
      isDarkMode,
      toggleTernaryDarkMode,
      setTernaryDarkMode,
      ternaryDarkMode,
      isSidebarExpanded,
      expandSidebar,
      collapseSidebar,
      onToggleSidebar,
      isPlanDetailsOpen,
      onTogglePlanDetails,
    ]
  );

  return (
    <DarkModeContext.Provider value={contextValue}>
      {children}
    </DarkModeContext.Provider>
  );
};
